import { Navigate, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { RequireCapability } from "@/components/RequireCapability";
import { getOnBehalfOf } from "@/lib/api/on-behalf";
import { useAuth } from "@/lib/auth/auth-context";

import { CreateProfileGate } from "./steps/ProfileGate";
import { ProfileEditor } from "./steps/ProfileEditor";

/**
 * Standalone enterprise profile editor, reached from the header user menu
 * ("Enterprise profile", between Settings and Log out). Reuses the same
 * ProfileEditor side-door the questionnaire exposes — this route just gives it a
 * direct entry point and a dashboard-oriented back link. Partner "on behalf of"
 * mode targets the member's profile; otherwise the user's own.
 */
export function EnterpriseProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Snapshot, not a per-render read, and for one reason: creating the profile
  // writes the new id into the cached `/auth/me` so the next route sees it. A
  // derived value would flip this component to `ProfileEditor` on that write —
  // while `CreateProfileGate`'s own mutation is still in flight — unmounting
  // the gate, dropping its observer, and with it the success toast and the
  // navigation to the questionnaire. `QuestionnaireExperience` holds it the
  // same way for the same reason.
  const [profileId] = useState(
    () => getOnBehalfOf() ?? user?.enterprise_profile_id ?? null,
  );

  if (!profileId) {
    // A member who has not created their profile yet gets the creation form,
    // not a redirect. This is where a training participant lands straight off
    // their verification link — AC 12 puts them on the enterprise-profile form
    // with no step in between, which is what the Django app does — and before
    // this they were bounced to the dashboard and never shown the form at all.
    //
    // `has_activateur_access` is the same predicate the questionnaire gates on,
    // and it is wider than it sounds: `is_partner_without_license` is one of
    // its branches, so a partner or local partner holding no licence of their
    // own *does* reach the creation form. That matches Django, whose
    // identification view sits behind the same gate, and the DRF create
    // endpoint, whose permission is `HasActivateurAccess`. What keeps the
    // redirect is an account the predicate refuses outright — an expert, or a
    // member whose subscription has lapsed.
    //
    // On-behalf-of mode cannot reach here at all: `getOnBehalfOf()` feeds
    // `profileId` above, so a partner consulting a member is never offered
    // profile creation.
    if (user?.has_activateur_access) {
      return (
        <RequireCapability required={["authenticated"]}>
          <CreateProfileGate
            onCreated={() => void navigate({ to: "/questionnaire" })}
          />
        </RequireCapability>
      );
    }
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <RequireCapability required={["authenticated"]}>
      <ProfileEditor
        profileId={profileId}
        onBack={() => void navigate({ to: "/dashboard" })}
        backLabel={t("questionnaire.nav.backToDashboard")}
      />
    </RequireCapability>
  );
}
